import React, { useEffect, useState } from "react";
import { Button, Card, Col, Modal, Result, Row, Skeleton } from "antd";
import { SanitizeHTML } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { Company } from "@bindeleddet/core/types";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import CompanyHeaderBox from "components/CompanyHeaderBox/CompanyHeaderBox";
import { DeleteOutlined, EditOutlined, LockOutlined, LoginOutlined } from "@ant-design/icons";
import { useLoginContext } from "@bindeleddet/core/contexts/login";
import AdminCompanyContacts from "./Sections/AdminCompanyContacts";
import AllCompanyEvents from "./Sections/AllCompanyEvents";
import AllCompanyJobs from "./Sections/AllCompanyJobs";
import DetailedInformation from "./Sections/DetailedInformation";
import EditCompanyModal from "./Sections/EditCompanyModal";
import ApproveCompany from "./Sections/ApproveCompany";
import CompanyComments from "./Sections/CompanyComments";
import ResetPasswordModal from "./Sections/ResetPasswordModal";

const AdminSingleCompany = () => {
  const [company, setCompany] = useState<Company>();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { logInAsCompany } = useLoginContext();
  const [editCompanyModalIsOpen, setEditCompanyModalIsOpen] = useState<boolean>(false);
  const [resetPasswordModalIsOpen, setResetPasswordModalIsOpen] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [loadingLoginAsCompany, setLoadingLoginAsCompany] = useState<boolean>(false);
  const [fetchingCompany, setFetchingCompany] = useState<boolean>(true);

  useEffect(() => {
    axios
      .get<Company>(`/companies/${id}/`)
      .then(response => setCompany(response.data))
      .finally(() => setFetchingCompany(false));
  }, [id]);

  const deleteCompany = () => {
    Modal.confirm({
      title: `Er du sikker på at du vil slette ${company?.name ?? "bedriften"}?`,
      content: "Denne handlingen kan ikke angres",
      okText: "Slett",
      okType: "danger",
      cancelText: "Avbryt",
      type: "error",
      onOk() {
        setLoadingDelete(true);
        axios
          .delete(`/companies/${id}/`)
          .then(() => {
            message("success", "Bedriften ble slettet");
            history.push("/admin/companies/");
            setLoadingDelete(false);
          })
          .catch(() => {
            message("error", "Feil ved sletting av bedrift");
            setLoadingDelete(false);
          });
      },
    });
  };

  if (fetchingCompany)
    return (
      <>
        <Row>
          <Skeleton.Button block active style={{ height: "125px" }} />
        </Row>
        <Row style={{ marginTop: "16px" }} gutter={[16, 16]}>
          <Col md={8} sm={24} xs={24}>
            <Skeleton.Button block active style={{ height: "350px" }} />
            <Skeleton.Button block active style={{ height: "150px", margin: "16px 0" }} />
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col md={16} sm={24} xs={24}>
            <Skeleton.Button block active style={{ height: "75px" }} />
            <Skeleton.Button block active style={{ height: "250px", margin: "16px 0" }} />
            <Skeleton.Button block active style={{ height: "250px" }} />
          </Col>
        </Row>
        <Row>
          <Skeleton.Button block active style={{ height: "75px", margin: "16px 0" }} />
        </Row>
      </>
    );

  if (!company)
    return <Result title="Fant ikke bedriften" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />;
  return (
    <>
      <EditCompanyModal
        company={company}
        open={editCompanyModalIsOpen}
        onClose={() => setEditCompanyModalIsOpen(false)}
        onSave={c => setCompany(c)}
      />
      {resetPasswordModalIsOpen && (
        <ResetPasswordModal company={company} onClose={() => setResetPasswordModalIsOpen(false)} />
      )}
      {!company.approved && <ApproveCompany company={company} handleApproveCompany={c => setCompany(c)} />}
      <CompanyHeaderBox company={company} />
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col md={8} sm={24}>
          <Row>
            <DetailedInformation company={company} />
          </Row>
          <Row>
            <AdminCompanyContacts companyId={company.id} />
          </Row>
          <Row>
            <Card title="Beskrivelse" style={{ width: "100%", margin: "16px auto" }}>
              <SanitizeHTML html={company.description} />
            </Card>
          </Row>
        </Col>
        <Col md={16} sm={24}>
          <Row>
            <Card style={{ width: "100%" }}>
              <Row gutter={[10, 10]} justify="space-between">
                <Col xl={6} lg={12} md={24} sm={24} xs={24}>
                  <Button
                    style={{ width: "100%" }}
                    onClick={() => {
                      setLoadingLoginAsCompany(true);
                      logInAsCompany(company.id)
                        .then(() => {
                          message("success", "Du er nå logget inn som bedriften");
                          setTimeout(() => {
                            window.location.href = "/company/company-profile/";
                          }, 100);
                        })
                        .catch(() => {
                          message("error", "Kunne ikke logge inn som bedriften");
                          setLoadingLoginAsCompany(false);
                        });
                    }}
                    icon={<LoginOutlined />}
                    loading={loadingLoginAsCompany}
                  >
                    Logg inn
                  </Button>
                </Col>
                <Col xl={8} lg={12} md={24} sm={24} xs={24}>
                  <Button
                    style={{ width: "100%" }}
                    onClick={() => setResetPasswordModalIsOpen(true)}
                    icon={<LockOutlined />}
                  >
                    Tilbakestill passord
                  </Button>
                </Col>
                <Col xl={5} lg={12} md={24} sm={24} xs={24}>
                  <Button
                    style={{ width: "100%" }}
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => setEditCompanyModalIsOpen(true)}
                  >
                    Rediger
                  </Button>
                </Col>
                <Col xl={5} lg={12} md={24} sm={24} xs={24}>
                  <Button
                    style={{ width: "100%" }}
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={deleteCompany}
                    loading={loadingDelete}
                  >
                    Slett
                  </Button>
                </Col>
              </Row>
            </Card>
          </Row>
          <Row style={{ margin: "16px auto" }}>
            <AllCompanyEvents companyId={company.id} />
          </Row>
          <Row>
            <AllCompanyJobs companyId={company.id} />
          </Row>
          <Row style={{ margin: "16px auto" }}>
            <CompanyComments companyId={company.id} />
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default AdminSingleCompany;
